
precision highp float;
precision highp sampler2D;


COLORING_FRAGMENT_DEFS

out vec4 fragColor;

uniform float opacity;
uniform float zoom;
uniform float time;
uniform sampler2D tex0;
uniform sampler2D tex1;
uniform float tilePixelSize;

// texture on the neighborhood
uniform sampler2D texN0;
uniform sampler2D texN1;
uniform bool availableN;
uniform sampler2D texE0;
uniform sampler2D texE1;
uniform bool availableE;
uniform sampler2D texS0;
uniform sampler2D texS1;
uniform bool availableS;
uniform sampler2D texW0;
uniform sampler2D texW1;
uniform bool availableW;
uniform float maxSmoothingDistance;
uniform float smoothingDistanceDecayFactor;
uniform bool localSmoothing;

// The difference between the view port zoom level (eg. 5.65) and the zoom level of the tile (eg. 5)
// (always positive, could be greater than 1. if maxZoom of the TileLayer is set below 22)
uniform float zoomDelta;

in vec2 tex0coord;
in vec2 tex1coord;
in vec2 mercCoord;

float PI = 3.141592;
const int halfNbSmoothingBins = int(ceil(float(NB_SMOOTHING_BINS)) / 2.);

/*

  texturePositionIndex refers to the neighborhood (N, NE, E, SE, S, SW, W, NW) of the central (C) texture.
  The index are as follow:
  - C  : 0
  - N  : 1
  - NE : 2
  - E  : 3
  - SE : 4
  - S  : 5
  - SW : 6
  - W  : 7
  - NW : 8

  of put spatially:

    8  1  2

    7  0  3

    6  5  4


  textureTimeIndex refers to the time-wise sampling. 0 means texture before (e.g. tex0 for the central texture)
  and 1 means the texture after (e.g. tex1 for the central texture)

  Thought because of the limit of 16 texture we can send to GPU, the following are never present: 2 8 4 and 6
  and the fallback for these positions are going to be on the central (external)

*/

// A uv can have one or both component (x, and/or y) that has a value below 0.0 or greater than 1.0.
// When this happens, it means the position to lookup is actually on one of the neighbor texture.
// newUv (out) is a vec2 that is in [0., 1.] relative to the relevant neighbor
// and texturePositionIndex (out) is the index of the neighbor as described above.
void textPositionToInternalTexPosition(vec2 uv, out vec2 newUv, out int texturePositionIndex) {

  // middle column
  if (uv.x >= 0. && uv.x <= 1.) {

    // middle row (C)
    if (uv.y >= 0. && uv.y <= 1.) {
      newUv = uv;
      texturePositionIndex = 0;

    // bottom row (S)
    } else if  (uv.y < 0. && availableS) {
      newUv = vec2(uv.x, 1. + uv.y);
      texturePositionIndex = 5;

    // top row (N)
    } else if  (uv.y > 1. && availableN) {
      newUv = vec2(uv.x, 1. - uv.y);
      texturePositionIndex = 1;
    } else {
      newUv = uv;
      texturePositionIndex = 0;
    }

  // left column
  } else if  (uv.x < 0.) {

    // middle row (W)
    if (uv.y >= 0. && uv.y <= 1. && availableW) {
      newUv = vec2(1. + uv.x, uv.y);
      texturePositionIndex = 7;
    } else {
      newUv = uv;
      texturePositionIndex = 0;
    }

  // right column
  } else if  (uv.x > 1.) {

    // middle row (E)
    if (uv.y >= 0. && uv.y <= 1. && availableE) {
      newUv = vec2(uv.x -1., uv.y);
      texturePositionIndex = 3;
    } else {
      newUv = uv;
      texturePositionIndex = 0;
    }
  }
}




vec4 getTextureColorByIndex(int textureTimeIndex, int texturePositionIndex, vec2 uv) {
  
  if (textureTimeIndex == 0) {
    if (texturePositionIndex == 0) {
      return texture(tex0, uv);
    } else if (texturePositionIndex == 1) {
      return texture(texN0, uv);
    } else if (texturePositionIndex == 3) {
      return texture(texE0, uv);
    } else if (texturePositionIndex == 5) {
      return texture(texS0, uv);
    } else if (texturePositionIndex == 7) {
      return texture(texW0, uv);
    }
  } else if (textureTimeIndex == 1) {
    if (texturePositionIndex == 0) {
      return texture(tex1, uv);
    } else if (texturePositionIndex == 1) {
      return texture(texN1, uv);
    } else if (texturePositionIndex == 3) {
      return texture(texE1, uv);
    } else if (texturePositionIndex == 5) {
      return texture(texS1, uv);
    } else if (texturePositionIndex == 7) {
      return texture(texW1, uv);
    }
  } else {
    return vec4(1., 0., 0., 1.);
  }
}


vec4 getColor(int textureTimeIndex, vec2 uv) {
  int texturePositionIndex = 0;
  vec2 localUV = vec2(0., 0.);
  textPositionToInternalTexPosition(uv, localUV, texturePositionIndex);
  return getTextureColorByIndex(textureTimeIndex, texturePositionIndex, localUV);
}


vec4 circularSampleSmoothing(int textureTimeIndex, vec2 uv, float resolution, float distance, out bool isTransparant) {
    
    
    vec4 colorRGBA = getColor(textureTimeIndex, uv);
    int weightCenter = 3;
    vec4 color = colorRGBA * float(weightCenter);

    isTransparant = colorRGBA.a < 1.;

    float angularStep = 2. * PI / float(halfNbSmoothingBins);

    for (int i = 0; i < halfNbSmoothingBins; i++) {
        float angle = angularStep * float(i);
        vec2 offset = vec2(cos(angle), sin(angle)) * distance * 0.5;
        vec2 pos = uv + offset / resolution;
        vec4 c = getColor(textureTimeIndex, pos);
        color += c;
        isTransparant = isTransparant || c.a < 1.;

        angle = angularStep / 2. + angularStep * float(i);
        offset = vec2(cos(angle), sin(angle)) * distance;
        pos = uv + offset / resolution;
        c = getColor(textureTimeIndex, pos);
        color += c;
        isTransparant = isTransparant || c.a < 1.;
    }

    return color / float(2 * halfNbSmoothingBins + weightCenter);
}


void main() {
  float scaledSamplingDistance = (maxSmoothingDistance * smoothingDistanceDecayFactor / (smoothingDistanceDecayFactor + zoom)) * max(1., 2. - zoomDelta);

  bool isTransparant0 = false;
  bool isTransparant1 = false;

  vec4 tex0Color;
  vec4 tex1Color;

  if (localSmoothing && halfNbSmoothingBins != 0) {
    tex0Color = circularSampleSmoothing(0, tex0coord, tilePixelSize, scaledSamplingDistance, isTransparant0);
    tex1Color = circularSampleSmoothing(1, tex1coord, tilePixelSize, scaledSamplingDistance, isTransparant1);
  } else {
    tex0Color = texture(tex0, tex0coord);
    tex1Color = texture(tex1, tex1coord);
  }

  vec4 inter = mix(tex0Color, tex1Color, time);

  if (inter.a < 1.0 && !RENDER_TRANSPARENT) discard;

  COLORING_FRAGMENT_CALLS

  fragColor.a *= opacity;
}
